/**
 * Client-side Web Push utilities.
 * Used in the patient lobby to subscribe to push notifications.
 */

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[Push] Web Push not supported in this browser');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('[Push] Service worker registered');
    return registration;
  } catch (err) {
    console.error('[Push] Service worker registration failed:', err);
    return null;
  }
}

export async function subscribeToPush(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    console.error('[Push] VAPID public key not configured');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('[Push] Notification permission denied');
    return null;
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
    console.log('[Push] Push subscription created');
    return subscription;
  } catch (err) {
    console.error('[Push] Push subscription failed:', err);
    return null;
  }
}

export async function registerWebPushToken(
  subscription: PushSubscription,
  userId: string,
  patientName: string,
): Promise<boolean> {
  try {
    const res = await fetch('/api/push-tokens', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        patient_name: patientName,
        platform: 'web-push',
        token: JSON.stringify(subscription.toJSON()),
      }),
    });
    return res.ok;
  } catch (err) {
    console.error('[Push] Token registration failed:', err);
    return false;
  }
}

/** Full flow: register SW → subscribe → send token to backend */
export async function enableWebPush(
  userId: string,
  patientName: string,
): Promise<boolean> {
  const reg = await registerServiceWorker();
  if (!reg) return false;

  const sub = await subscribeToPush(reg);
  if (!sub) return false;

  return registerWebPushToken(sub, userId, patientName);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
